import React from 'react';
import ReactDOM from 'react-dom/client';
import { Provider } from 'react-redux';

// Suppress React warning about defaultProps in function components (from external libraries)
const originalError = console.error;
console.error = (...args) => {
  if (
    typeof args[0] === 'string' &&
    args[0].includes('Support for defaultProps will be removed from function components')
  ) {
    return; // Suppress this specific warning
  }
  originalError.call(console, ...args);
};

import './assets/css/styles.css';
// Package CSS (workspace-hoist safe). Avoid apps/src/.../node_modules relative font URLs.
import 'font-awesome/css/font-awesome.min.css';
import '@fortawesome/fontawesome-free/css/all.min.css';
import './assets/css/bootstrap.min.css';

import './assets/js/bootstrap.bundle.min.js';

import { store } from './helpers';
import { App } from './App';
import ErrorBoundary from './components/ErrorBoundary';
import { getObservabilityContext, initFaro, pushFaroEvent, isObservabilityEnabled, getAppName } from './observability';
import { history } from './helpers';

getObservabilityContext();
initFaro();

if (isObservabilityEnabled() && history && history.listen) {
    history.listen((location) => {
        if (location && location.pathname) {
            pushFaroEvent('route_change', { app_name: getAppName(), path: location.pathname });
        }
    });
}

const REQUIRED_ENV = {
    REACT_APP_API_URL: process.env.REACT_APP_API_URL,
};

function getMissingEnv() {
    const missing = Object.entries(REQUIRED_ENV)
        .filter(([, value]) => value === undefined || value === '' || String(value).trim() === '')
        .map(([key]) => key);
    return missing;
}

function ConfigError({ missing }) {
    return (
        <div
            style={{
                padding: '2rem',
                textAlign: 'center',
                fontFamily: 'sans-serif',
                minHeight: '100vh',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                boxSizing: 'border-box',
            }}
        >
            <h1 style={{ marginBottom: '1rem', color: '#333' }}>Configuration missing</h1>
            <p style={{ color: '#666', maxWidth: '480px' }}>
                The app cannot start because required environment variables are not set.
            </p>
            <p style={{ color: '#888', fontSize: '0.875rem', marginTop: '0.5rem' }}>
                Missing: {missing.join(', ')}
            </p>
            <p style={{ color: '#888', fontSize: '0.875rem', marginTop: '1rem' }}>
                Set them in .env or your deployment configuration (e.g. REACT_APP_API_URL).
            </p>
        </div>
    );
}

// Log unhandled promise rejections so they are visible in production (e.g. in monitoring tools)
window.addEventListener('unhandledrejection', (event) => {
  console.error('Unhandled promise rejection:', event.reason);
});

const root = ReactDOM.createRoot(document.getElementById('app'));
const missingEnv = getMissingEnv();

if (missingEnv.length > 0) {
    root.render(<ConfigError missing={missingEnv} />);
} else {
    root.render(
        <Provider store={store}>
            <ErrorBoundary>
                <App />
            </ErrorBoundary>
        </Provider>
    );
}
